import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Award, Users, ExternalLink, Tag } from "lucide-react";
import { SignupOffer } from "../../types";

interface SignupOfferWelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: SignupOffer;
  username?: string;
}

export const SignupOfferWelcomeModal: React.FC<SignupOfferWelcomeModalProps> = ({
  isOpen,
  onClose,
  offer,
  username,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md mx-4 bg-[#0A0A0A] border border-primary/30 rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative background glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-primary/20 rounded-full blur-[100px] pointer-events-none" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Content */}
          <div className="relative p-6 text-center">
            {/* Badge animation - smaller */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", damping: 15 }}
              className="mb-4"
            >
              {offer.badgeImageUrl ? (
                <div className="relative inline-block">
                  <img
                    src={offer.badgeImageUrl}
                    alt={offer.name}
                    className="w-16 h-16 mx-auto"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-primary"
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
              ) : (
                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/30 to-primary/10">
                  <Award className="w-8 h-8 text-primary" />
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-primary"
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
              )}
            </motion.div>

            {/* Welcome Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-black text-white mb-1"
            >
              {offer.welcomeTitle}
            </motion.h2>

            {/* Username highlight */}
            {username && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-primary font-semibold text-sm mb-3"
              >
                Welcome, @{username}!
              </motion.p>
            )}

            {/* Welcome Message - shorter */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-white/70 text-sm leading-relaxed mb-5"
            >
              You're now a founding member shaping the future of sports fandom.
            </motion.p>

            {/* Rewards received - compact */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-2 mb-5"
            >
              {/* GT Bonus */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-white text-sm">GroundTokens</span>
                </div>
                <span className="text-primary font-black text-lg">
                  +{offer.gtBonus.toLocaleString()} GT
                </span>
              </div>

              {/* Badge */}
              {offer.badgeId && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <Award className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-white text-sm">{offer.name} Badge</span>
                  </div>
                  <span className="text-primary font-semibold text-sm">Unlocked</span>
                </div>
              )}

              {/* Profile Tag */}
              {offer.tagId && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <Tag className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-white text-sm">OG Profile Tag</span>
                  </div>
                  <span className="px-2 py-0.5 bg-primary rounded-full text-black text-xs font-bold">
                    OG
                  </span>
                </div>
              )}
            </motion.div>

            {/* Discord Link - compact */}
            {offer.discordLink && (
              <motion.a
                href={offer.discordLink}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center justify-center gap-2 p-3 rounded-xl bg-[#5865F2]/20 border border-[#5865F2]/30 text-[#5865F2] hover:bg-[#5865F2]/30 transition-colors mb-4"
              >
                <Users className="w-4 h-4" />
                <span className="font-semibold text-sm">Join Discord Beta Testers</span>
                <ExternalLink className="w-3 h-3" />
              </motion.a>
            )}

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              onClick={onClose}
              className="w-full py-3 bg-primary text-black font-bold rounded-full shadow-neon hover:shadow-neon-lg transition-all duration-300"
            >
              Let's Go!
            </motion.button>

            {/* Footer text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-text-muted text-xs mt-3"
            >
              Your rewards have been added to your wallet
            </motion.p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SignupOfferWelcomeModal;
